import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type {
  CreateWorkflowCustomFieldRequest,
  DeleteWorkflowCustomFieldRequest,
  GetWorkflowCustomFieldsResponse,
  LinkProjectReusableFieldToWorkflowRequest,
  ProjectReusableFieldListItem,
  UpdateWorkflowCustomFieldRequest,
} from '../contracts/workflow-custom-fields.contract';
import type { CustomFieldViewModel } from '../models/custom-field.model';
import type { FormulaDefinition } from '../models/formula-field.model';
import {
  alignFormulaFieldIdsToWorkflow,
  clampFormulaResultDecimalPlaces,
  formulaDefinitionNeedsApiOperands,
  parseFormulaDefinition,
  parseFormulaDefinitionFromApiOperands,
  parseFormulaDefinitionFromContractFormula,
  toFormulaOperandsApiPayload,
} from '../models/formula-field.model';
import { clampRatingLevels } from '../utils/rating-field.util';
import { clampNumberFieldDecimalPlaces } from '../utils/number-field.util';
import {
  clampCodePrefix,
  codeFormatFromApi,
  codeFormatToApi,
  codeSeparatorFromApi,
  codeSeparatorToApi,
  normalizeCodeFormat,
  normalizeCodeLength,
  normalizeCodeSeparator,
  CODE_FORMAT_DEFAULT,
  CODE_LENGTH_DEFAULT,
  CODE_PREFIX_DEFAULT,
  CODE_SEPARATOR_DEFAULT,
} from '../utils/code-field.util';

/** GUID que a API usa para linhas de “campo nativo” do card em `/api/workflow-fields/workflow/:id` — não é um workflow-field real. */
const PLACEHOLDER_WORKFLOW_FIELD_ID = '00000000-0000-0000-0000-000000000000';

@Injectable({ providedIn: 'root' })
export class CustomFieldsSchemaDataService {
  private readonly http = inject(HttpClient);
  private readonly schemaSignal = signal<Record<string, CustomFieldViewModel[]>>({});
  private readonly fieldKeysById = new Map<string, string>();
  private readonly formulaResultDecimalPlacesOverlayById = new Map<string, number>();
  private readonly formulaDetailIdByWorkflowFieldId = new Map<string, string>();
  private formulaEndpointUnavailable = false;
  /**
   * GET do workflow muitas vezes ainda não devolve `numberDecimalPlaces` / `numberShowPercentSuffix`.
   * Guardamos o último valor gravado com sucesso para a UI e o `CustomFieldsDataService` ficarem coerentes.
   */
  private readonly numberFieldOverlayById = new Map<
    string,
    { numberDecimalPlaces: number; numberShowPercentSuffix: boolean }
  >();

  async getWorkflowCustomFields(workflowId: string): Promise<GetWorkflowCustomFieldsResponse> {
    const response = await firstValueFrom(
      this.http.get<unknown>(`/api/workflow-fields/workflow/${workflowId}`)
    );
    const items = this.filterWorkflowFieldEntries(response);
    const normalizedFormulas = await this.mapWorkflowFieldEntriesToViewModels(workflowId, items);
    const withOverlays = this.applyNumberFieldOverlaysToList(normalizedFormulas);
    this.schemaSignal.update((current) => ({ ...current, [workflowId]: withOverlays }));
    return { workflowId, fields: withOverlays };
  }

  /**
   * Campos do workflow vindos de GET /api/cards/{id}/detail (`workflowFields`), já com options em linha.
   * Atualiza o cache do schema como o GET por workflow.
   */
  async ingestWorkflowFieldsFromDetailPayload(
    workflowId: string,
    rawWorkflowFields: unknown
  ): Promise<CustomFieldViewModel[]> {
    const items = this.filterWorkflowFieldEntries(rawWorkflowFields);
    if (!items.length) {
      return (await this.getWorkflowCustomFields(workflowId)).fields;
    }
    const normalizedFormulas = await this.mapWorkflowFieldEntriesToViewModels(workflowId, items);
    const withOverlays = this.applyNumberFieldOverlaysToList(normalizedFormulas);
    this.schemaSignal.update((current) => ({ ...current, [workflowId]: withOverlays }));
    return withOverlays;
  }

  private filterWorkflowFieldEntries(response: unknown): Record<string, unknown>[] {
    return this.resolveItems(response).filter((entry) => !this.shouldHideAsNativeCardArtifact(entry));
  }

  private async mapWorkflowFieldEntriesToViewModels(
    _workflowId: string,
    items: Record<string, unknown>[]
  ): Promise<CustomFieldViewModel[]> {
    const mapped = await Promise.all(
      items.map(async (entry, index) => {
        const nestedField = this.asRecord(entry['field'] ?? entry['Field']);
        const source = { ...nestedField, ...entry };
        return this.mapApiSourceToFieldViewModel(source, index);
      })
    );

    const fields = await this.enrichFormulaFieldsWithDetails(mapped);

    const normalized = fields
      .filter(
        (field) =>
          !!field.fieldId && !!field.name && !this.isPlaceholderWorkflowFieldId(field.fieldId)
      )
      .sort((a, b) => a.order - b.order);

    const workflowFieldIdSet = new Set(normalized.map((f) => f.fieldId));
    return normalized.map((field) => {
      if (field.type !== 'FORMULA' || !field.formulaDefinition) return field;
      return {
        ...field,
        formulaDefinition: alignFormulaFieldIdsToWorkflow(
          field.formulaDefinition,
          workflowFieldIdSet
        ),
      };
    });
  }

  /**
   * Alguns ambientes ainda não devolvem `formulaResultDecimalPlaces` no GET de lista.
   * Fallback: consulta o GET por id para cada fórmula sem casas decimais explícitas.
   */
  private async enrichFormulaFieldsWithDetails(
    fields: CustomFieldViewModel[]
  ): Promise<CustomFieldViewModel[]> {
    const needs = fields.filter(
      (f) => f.type === 'FORMULA' && !!f.fieldId && (f.formulaResultDecimalPlaces ?? 0) === 0
    );
    if (!needs.length) return fields;
    const byId = new Map(fields.map((f) => [f.fieldId, f] as const));
    await Promise.all(
      needs.map(async (f) => {
        try {
          const detailed = await this.getWorkflowCustomFieldById(f.fieldId);
          if (!detailed || detailed.type !== 'FORMULA') return;
          const current = byId.get(f.fieldId);
          if (!current) return;
          byId.set(f.fieldId, {
            ...current,
            formulaDefinition: detailed.formulaDefinition ?? current.formulaDefinition,
            formulaResultDecimalPlaces: clampFormulaResultDecimalPlaces(
              detailed.formulaResultDecimalPlaces
            ),
          });
        } catch {
          /* ignore fallback failure */
        }
      })
    );
    return fields.map((f) => byId.get(f.fieldId) ?? f);
  }

  /** GET /api/workflow-fields/{id} — contrato com `formula.operands` (camelCase). */
  async getWorkflowCustomFieldById(fieldId: string): Promise<CustomFieldViewModel | null> {
    if (this.isPlaceholderWorkflowFieldId(fieldId)) return null;
    const raw = await firstValueFrom(
      this.http.get<unknown>(`/api/workflow-fields/${encodeURIComponent(fieldId)}`)
    );
    const source = this.asRecord(raw);
    const wid = this.readString(this.pickValue(source, ['workflowId', 'WorkflowId']));
    if (!wid) return null;
    let field = await this.mapApiSourceToFieldViewModel(source, 0);
    if (!field.fieldId) return null;
    if (field.type === 'FORMULA') {
      const formulaDetailId =
        this.readString(
          this.pickValue(source, [
            'customFieldId',
            'CustomFieldId',
            'sourceCustomFieldId',
            'SourceCustomFieldId',
          ])
        ) ||
        this.formulaDetailIdByWorkflowFieldId.get(this.normalizeNumberFieldOverlayKey(field.fieldId)) ||
        field.fieldId;
      const formulaDetail = await this.getFormulaDetailByFieldId(formulaDetailId);
      if (formulaDetail) {
        field = {
          ...field,
          formulaDefinition: formulaDetail.formulaDefinition ?? field.formulaDefinition,
          formulaResultDecimalPlaces: formulaDetail.formulaResultDecimalPlaces,
        };
      }
    }
    field = this.applyNumberFieldOverlayToField(field);
    const siblings = this.schemaSignal()[wid] ?? [];
    const idSet = new Set(siblings.map((f) => f.fieldId));
    if (field.type === 'FORMULA' && field.formulaDefinition && idSet.size > 0) {
      return {
        ...field,
        formulaDefinition: alignFormulaFieldIdsToWorkflow(field.formulaDefinition, idSet),
      };
    }
    return field;
  }

  /**
   * GET /api/custom-fields/{id}/formula — fonte dedicada para `resultDecimalPlaces` no ecrã de edição.
   */
  private async getFormulaDetailByFieldId(fieldId: string): Promise<{
    formulaDefinition: FormulaDefinition | null;
    formulaResultDecimalPlaces: number;
  } | null> {
    if (this.formulaEndpointUnavailable) return null;
    let raw: unknown;
    try {
      raw = await firstValueFrom(
        this.http.get<unknown>(`/api/custom-fields/${encodeURIComponent(fieldId)}/formula`)
      );
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'status' in err && (err as { status: number }).status === 404) {
        this.formulaEndpointUnavailable = true;
      }
      return null;
    }
    const source = this.asRecord(raw);
    if (!Object.keys(source).length) return null;

    const rawOperands =
      this.pickValue(source, ['operands', 'Operands']) ??
      this.pickValue(source, ['formulaOperands', 'FormulaOperands']);
    let formulaDefinition = parseFormulaDefinitionFromApiOperands(rawOperands);
    if (!formulaDefinition) {
      const formulaBlock = this.asRecord(this.pickValue(source, ['formula', 'Formula']));
      if (Object.keys(formulaBlock).length > 0) {
        formulaDefinition = parseFormulaDefinitionFromContractFormula(formulaBlock);
      }
    }
    if (!formulaDefinition) {
      formulaDefinition = parseFormulaDefinition(
        this.pickValue(source, ['formulaJson', 'FormulaJson', 'formulaDefinition', 'FormulaDefinition'])
      );
    }

    const dpRaw = this.pickValue(source, [
      'resultDecimalPlaces',
      'ResultDecimalPlaces',
      'formulaResultDecimalPlaces',
      'FormulaResultDecimalPlaces',
    ]);
    const dp =
      typeof dpRaw === 'number'
        ? dpRaw
        : typeof dpRaw === 'string'
          ? Number(dpRaw.trim())
          : NaN;

    return {
      formulaDefinition,
      formulaResultDecimalPlaces: clampFormulaResultDecimalPlaces(Number.isFinite(dp) ? dp : 0),
    };
  }

  private async mapApiSourceToFieldViewModel(
    source: Record<string, unknown>,
    index: number
  ): Promise<CustomFieldViewModel> {
    const fieldId = this.readString(
      this.pickValue(source, [
        'id',
        'Id',
        'fieldId',
        'FieldId',
        'workflowFieldId',
        'WorkflowFieldId',
      ])
    );
    const fieldType = this.resolveWorkflowFieldTypeFromApi(source);
    const formulaDetailId = this.readString(
      this.pickValue(source, [
        'customFieldId',
        'CustomFieldId',
        'sourceCustomFieldId',
        'SourceCustomFieldId',
      ])
    );
    if (fieldId && formulaDetailId) {
      this.formulaDetailIdByWorkflowFieldId.set(
        this.normalizeNumberFieldOverlayKey(fieldId),
        formulaDetailId
      );
    }
    /** GET /api/workflow-fields/workflow/{id} inclui `options` em linha — evita N+1 em loadOptions. */
    let options =
      (fieldType === 'SELECT' || fieldType === 'ROW_COLOR') ? this.parseInlineWorkflowFieldOptions(source) : [];
    if ((fieldType === 'SELECT' || fieldType === 'ROW_COLOR') && options.length === 0) {
      options = await this.loadOptions(fieldId);
    }
    const label = this.readString(this.pickValue(source, ['label', 'Label', 'name', 'Name']));
    const key = this.readString(this.pickValue(source, ['key', 'Key'])) || this.slugify(label);
    this.fieldKeysById.set(fieldId, key);

    const formulaBlock = this.asRecord(this.pickValue(source, ['formula', 'Formula']));
    const rawFormula = this.pickValue(source, [
      'formulaJson',
      'FormulaJson',
      'formulaDefinition',
      'FormulaDefinition',
    ]);
    let rawFormulaOperands = this.pickValue(source, [
      'formulaOperands',
      'FormulaOperands',
    ]);
    if (rawFormulaOperands == null && rawFormula != null) {
      if (typeof rawFormula === 'object' && !Array.isArray(rawFormula)) {
        const fr = rawFormula as Record<string, unknown>;
        rawFormulaOperands = fr['formulaOperands'] ?? fr['FormulaOperands'];
      } else if (typeof rawFormula === 'string') {
        const t = rawFormula.trim();
        if (t.startsWith('{')) {
          try {
            const fr = JSON.parse(t) as Record<string, unknown>;
            rawFormulaOperands = fr['formulaOperands'] ?? fr['FormulaOperands'];
          } catch {
            /* ignore */
          }
        }
      }
    }
    if (rawFormulaOperands == null && Object.keys(formulaBlock).length > 0) {
      rawFormulaOperands =
        formulaBlock['formulaOperands'] ?? formulaBlock['FormulaOperands'];
    }
    if (rawFormulaOperands == null) {
      const found = this.findValueByNormalizedKeys(source, new Set(['formulaoperands']), 6);
      if (Array.isArray(found)) rawFormulaOperands = found;
    }

    let formulaDefinition: FormulaDefinition | null = null;
    if (fieldType === 'FORMULA') {
      if (Object.keys(formulaBlock).length > 0) {
        formulaDefinition = parseFormulaDefinitionFromContractFormula(formulaBlock);
      }
      if (!formulaDefinition) {
        formulaDefinition = parseFormulaDefinition(rawFormula);
      }
      if (formulaDefinitionNeedsApiOperands(formulaDefinition)) {
        const fromOps = parseFormulaDefinitionFromApiOperands(rawFormulaOperands);
        if (fromOps) formulaDefinition = fromOps;
      } else if (!formulaDefinition) {
        formulaDefinition = parseFormulaDefinitionFromApiOperands(rawFormulaOperands);
      }
    }

    const formulaResultDecimalPlaces =
      fieldType === 'FORMULA'
        ? this.readFormulaResultDecimalPlaces(formulaBlock, rawFormula, source)
        : 0;

    const ratingLevels = this.readRatingLevels(source, fieldType);

    const numberDecimalPlaces = this.readNumberFieldDecimalPlaces(source, fieldType);
    const numberShowPercentSuffix = this.readNumberShowPercentSuffix(source, fieldType);

    const codeBlock = fieldType === 'CODE'
      ? this.asRecord(this.pickValue(source, ['code', 'Code']))
      : {};
    const codePrefix = fieldType === 'CODE'
      ? clampCodePrefix(
          this.readString(
            this.pickValue(codeBlock, ['prefix', 'Prefix']) ??
            this.pickValue(source, ['codePrefix', 'CodePrefix', 'prefix', 'Prefix'])
          )
        )
      : CODE_PREFIX_DEFAULT;
    const codeFormat = fieldType === 'CODE'
      ? codeFormatFromApi(
          this.pickValue(codeBlock, ['bodyFormat', 'BodyFormat']) ??
          this.pickValue(source, ['codeBodyFormat', 'CodeBodyFormat', 'codeFormat', 'CodeFormat'])
        )
      : CODE_FORMAT_DEFAULT;
    const codeLength = fieldType === 'CODE'
      ? normalizeCodeLength(
          this.pickValue(codeBlock, ['bodyLength', 'BodyLength']) ??
          this.pickValue(source, ['codeBodyLength', 'CodeBodyLength', 'codeLength', 'CodeLength'])
        )
      : CODE_LENGTH_DEFAULT;
    const codeSeparator = fieldType === 'CODE'
      ? codeSeparatorFromApi(
          this.pickValue(codeBlock, ['separator', 'Separator']) ??
          this.pickValue(source, ['codeSeparator', 'CodeSeparator'])
        )
      : CODE_SEPARATOR_DEFAULT;

    const projectReusableFieldIdRaw = this.readString(
      this.pickValue(source, [
        'projectReusableFieldId',
        'ProjectReusableFieldId',
        'reusableFieldId',
        'ReusableFieldId',
      ])
    );
    const projectReusableFieldId = projectReusableFieldIdRaw || null;

    /**
     * Badge “reutilizável”: preferir flag explícita do backend.
     * Só `ProjectReusableFieldId` também preenchido em “salvar como reutilizável”; sem flag, o badge pode ficar oculto.
     */
    const fromProjectReusable = this.readBoolean(
      this.pickBooleanLikeValue(source, [
        'isLinkedFromProjectReusable',
        'IsLinkedFromProjectReusable',
        'isCreatedFromProjectReusable',
        'IsCreatedFromProjectReusable',
        'createdFromProjectReusable',
        'CreatedFromProjectReusable',
        'fromProjectReusable',
        'FromProjectReusable',
        'linkedFromProjectReusable',
        'LinkedFromProjectReusable',
      ])
    );

    const personRestrictToWorkflowMembers =
      fieldType === 'PERSON' || fieldType === 'USER'
        ? this.readBoolean(
            this.pickBooleanLikeValue(source, [
              'personRestrictToWorkflowMembers',
              'PersonRestrictToWorkflowMembers',
            ])
          )
        : false;

    const collectViaWhatsApp = this.readBoolean(
      this.pickBooleanLikeValue(source, ['collectViaWhatsApp', 'CollectViaWhatsApp'])
    );
    const whatsAppPrompt =
      this.readString(this.pickValue(source, ['whatsAppPrompt', 'WhatsAppPrompt'])) || null;
    const whatsAppCollectionOrderRaw = this.pickValue(source, [
      'whatsAppCollectionOrder',
      'WhatsAppCollectionOrder',
    ]);
    const whatsAppCollectionOrderNum =
      typeof whatsAppCollectionOrderRaw === 'number'
        ? whatsAppCollectionOrderRaw
        : whatsAppCollectionOrderRaw == null || whatsAppCollectionOrderRaw === ''
          ? NaN
          : Number(whatsAppCollectionOrderRaw);
    const whatsAppCollectionOrder = Number.isFinite(whatsAppCollectionOrderNum)
      ? whatsAppCollectionOrderNum
      : null;

    return {
      fieldId,
      key,
      name: label,
      type: fieldType,
      required: this.readBoolean(this.pickBooleanLikeValue(source, REQUIRED_KEYS)),
      readOnly: this.readBoolean(this.pickBooleanLikeValue(source, READONLY_KEYS)),
      showInQuickView: this.readBoolean(this.pickBooleanLikeValue(source, QUICK_VIEW_KEYS)),
      showInApp: this.readBoolean(this.pickBooleanLikeValue(source, SHOW_IN_APP_KEYS)),
      options,
      order: this.readNumber(this.pickValue(source, ['order', 'Order']), index + 1),
      placeholder: null,
      formulaDefinition,
      formulaResultDecimalPlaces,
      numberDecimalPlaces,
      numberShowPercentSuffix,
      ratingLevels,
      codePrefix,
      codeFormat,
      codeLength,
      codeSeparator,
      projectReusableFieldId,
      fromProjectReusable,
      personRestrictToWorkflowMembers,
      collectViaWhatsApp,
      whatsAppPrompt,
      whatsAppCollectionOrder,
    } satisfies CustomFieldViewModel;
  }

  private readNumberFieldDecimalPlaces(
    source: Record<string, unknown>,
    fieldType: CustomFieldViewModel['type']
  ): number {
    if (fieldType !== 'NUMBER') return 0;
    const raw = this.pickValue(source, [
      'numberDecimalPlaces',
      'NumberDecimalPlaces',
      'numericDecimalPlaces',
      'NumericDecimalPlaces',
      'decimalPlaces',
      'DecimalPlaces',
    ]);
    // Omissão na API deve manter comportamento conservador (inteiro).
    // Evita “aparecer 2 casas” em campos antigos que eram inteiros.
    if (raw === undefined || raw === null || raw === '') {
      return 0;
    }
    const n =
      typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(String(raw).trim()) : NaN;
    if (!Number.isFinite(n)) {
      return 0;
    }
    return clampNumberFieldDecimalPlaces(n);
  }

  private readNumberShowPercentSuffix(
    source: Record<string, unknown>,
    fieldType: CustomFieldViewModel['type']
  ): boolean {
    if (fieldType !== 'NUMBER') return false;
    return this.readBoolean(
      this.pickBooleanLikeValue(source, [
        'numberShowPercentSuffix',
        'NumberShowPercentSuffix',
        'showPercentSuffix',
        'ShowPercentSuffix',
      ])
    );
  }

  private readRatingLevels(
    source: Record<string, unknown>,
    fieldType: CustomFieldViewModel['type']
  ): number {
    if (fieldType !== 'RATING') return clampRatingLevels(undefined);
    const raw = this.pickValue(source, [
      'ratingLevels',
      'RatingLevels',
      'maxRating',
      'MaxRating',
      'ratingScale',
      'RatingScale',
    ]);
    return clampRatingLevels(raw);
  }

  private readFormulaResultDecimalPlaces(
    formulaBlock: Record<string, unknown>,
    rawFormula: unknown,
    source: Record<string, unknown>
  ): number {
    const fromBlock = this.pickValue(formulaBlock, ['resultDecimalPlaces', 'ResultDecimalPlaces']);
    let n =
      typeof fromBlock === 'number'
        ? fromBlock
        : typeof fromBlock === 'string'
          ? Number(fromBlock)
          : NaN;
    if (!Number.isFinite(n)) {
      const fromRaw = this.readFormulaResultDecimalPlacesFromRawFormula(rawFormula);
      if (fromRaw != null) {
        n = fromRaw;
      }
    }
    if (!Number.isFinite(n)) {
      const top = this.pickValue(source, [
        'formulaResultDecimalPlaces',
        'FormulaResultDecimalPlaces',
        'resultDecimalPlaces',
        'ResultDecimalPlaces',
        'decimalPlaces',
        'DecimalPlaces',
      ]);
      n =
        typeof top === 'number' ? top : typeof top === 'string' ? Number(top) : NaN;
    }
    return clampFormulaResultDecimalPlaces(Number.isFinite(n) ? n : 0);
  }

  private readFormulaResultDecimalPlacesFromRawFormula(rawFormula: unknown): number | null {
    if (rawFormula == null) return null;
    let rec: Record<string, unknown> | null = null;
    if (typeof rawFormula === 'object' && !Array.isArray(rawFormula)) {
      rec = rawFormula as Record<string, unknown>;
    } else if (typeof rawFormula === 'string') {
      const t = rawFormula.trim();
      if (t.startsWith('{')) {
        try {
          const parsed = JSON.parse(t) as unknown;
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            rec = parsed as Record<string, unknown>;
          }
        } catch {
          return null;
        }
      }
    }
    if (!rec) return null;
    const v = rec['resultDecimalPlaces'] ?? rec['ResultDecimalPlaces'];
    const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
    return Number.isFinite(n) ? clampFormulaResultDecimalPlaces(n) : null;
  }

  async createWorkflowCustomField(request: CreateWorkflowCustomFieldRequest): Promise<CustomFieldViewModel> {
    const fieldTypeNumber = this.toApiFieldType(request.field.type);
    const key = this.slugify(request.field.name);
    const payload: Record<string, unknown> = {
      workflowId: request.workflowId,
      label: request.field.name.trim(),
      key,
      fieldType: fieldTypeNumber,
      isRequired: request.field.required === true,
      isImportable: true,
      isReadOnly: request.field.readOnly === true,
      showInQuickView: request.field.showInQuickView === true,
      showInApp: request.field.showInApp === true,
      collectViaWhatsApp: request.field.collectViaWhatsApp === true,
      whatsAppPrompt: request.field.whatsAppPrompt ?? null,
      whatsAppCollectionOrder: request.field.whatsAppCollectionOrder ?? null,
    };
    const formulaDp = clampFormulaResultDecimalPlaces(
      request.field.type === 'FORMULA' ? request.field.formulaResultDecimalPlaces : 0
    );
    if (fieldTypeNumber === 14) {
      payload['formulaResultDecimalPlaces'] = formulaDp;
      if (request.field.formulaDefinition) {
        payload['formulaOperands'] = toFormulaOperandsApiPayload(request.field.formulaDefinition);
      }
    }
    if (fieldTypeNumber === 15) {
      payload['ratingLevels'] = clampRatingLevels(request.field.ratingLevels);
    }
    if (fieldTypeNumber === 3) {
      const numberDecimalPlaces = clampNumberFieldDecimalPlaces(
        request.field.type === 'NUMBER' ? request.field.numberDecimalPlaces : 0
      );
      const numberShowPercentSuffix =
        request.field.type === 'NUMBER' && request.field.numberShowPercentSuffix === true;
      payload['numberDecimalPlaces'] = numberDecimalPlaces;
      payload['decimalPlaces'] = numberDecimalPlaces;
      payload['numberShowPercentSuffix'] = numberShowPercentSuffix;
      payload['showPercentSuffix'] = numberShowPercentSuffix;
    }
    if (fieldTypeNumber === 16) {
      payload['codePrefix'] = clampCodePrefix(request.field.codePrefix);
      payload['codeBodyFormat'] = codeFormatToApi(request.field.codeFormat);
      payload['codeBodyLength'] = normalizeCodeLength(request.field.codeLength);
      payload['codeSeparator'] = codeSeparatorToApi(request.field.codeSeparator);
    }
    /** Só PERSON aceita a restrição; nos demais tipos o campo nem vai no payload. */
    if (fieldTypeNumber === 9) {
      payload['personRestrictToWorkflowMembers'] =
        request.field.personRestrictToWorkflowMembers === true;
    }
    /** Backend: `CreateWorkflowFieldRequest.SaveAsProjectReusable` */
    if (request.saveAsProjectReusable === true) {
      payload['saveAsProjectReusable'] = true;
    }
    const created = this.asRecord(await firstValueFrom(this.http.post<unknown>('/api/workflow-fields', payload)));
    const fieldId = this.readString(this.pickValue(created, ['id', 'Id']));
    const createdProjectReusableFieldId = this.readString(
      this.pickValue(created, ['projectReusableFieldId', 'ProjectReusableFieldId'])
    );

    if (fieldTypeNumber === 14 && fieldId && request.field.formulaDefinition) {
      await this.putFormulaOperands(fieldId, request.field.formulaDefinition, formulaDp);
    }
    if (fieldTypeNumber === 14 && fieldId) {
      this.recordFormulaFieldOverlay(fieldId, formulaDp);
    }

    if ((fieldTypeNumber === 6 || fieldTypeNumber === 21) && fieldId && request.field.options.length) {
      for (const option of request.field.options) {
        await this.createOrUpdateOption(
          null,
          fieldId,
          option.label,
          option.value,
          option.colorHex,
          option.order
        );
      }
    }

    if (request.field.type === 'NUMBER' && fieldId) {
      this.recordNumberFieldOverlayFromSave(fieldId, request.field);
    }

    const response = await this.getWorkflowCustomFields(request.workflowId);
    const createdField =
      response.fields.find((field) => field.fieldId === fieldId) ??
      response.fields.find((field) => this.slugify(field.name) === key);
    if (createdField) return createdField;
    const prfFromResponse = createdProjectReusableFieldId || null;
    return {
      fieldId: fieldId || `cf-${Date.now()}`,
      key,
      name: request.field.name.trim(),
      type: request.field.type,
      required: request.field.required,
      readOnly: request.field.readOnly,
      showInQuickView: request.field.showInQuickView === true,
      showInApp: request.field.showInApp === true,
      options: request.field.options,
      order: this.readNumber(this.pickValue(created, ['order', 'Order']), 1),
      placeholder: request.field.placeholder,
      formulaDefinition:
        request.field.type === 'FORMULA' ? request.field.formulaDefinition : null,
      formulaResultDecimalPlaces:
        request.field.type === 'FORMULA' ? formulaDp : 0,
      numberDecimalPlaces:
        request.field.type === 'NUMBER'
          ? clampNumberFieldDecimalPlaces(request.field.numberDecimalPlaces)
          : 0,
      numberShowPercentSuffix:
        request.field.type === 'NUMBER' ? request.field.numberShowPercentSuffix === true : false,
      ratingLevels: clampRatingLevels(request.field.ratingLevels),
      codePrefix: request.field.type === 'CODE' ? clampCodePrefix(request.field.codePrefix) : CODE_PREFIX_DEFAULT,
      codeFormat: request.field.type === 'CODE' ? normalizeCodeFormat(request.field.codeFormat) : CODE_FORMAT_DEFAULT,
      codeLength: request.field.type === 'CODE' ? normalizeCodeLength(request.field.codeLength) : CODE_LENGTH_DEFAULT,
      codeSeparator: request.field.type === 'CODE' ? normalizeCodeSeparator(request.field.codeSeparator) : CODE_SEPARATOR_DEFAULT,
      projectReusableFieldId: prfFromResponse,
      fromProjectReusable: false,
      personRestrictToWorkflowMembers:
        request.field.type === 'PERSON' &&
        request.field.personRestrictToWorkflowMembers === true,
      collectViaWhatsApp: request.field.collectViaWhatsApp === true,
      whatsAppPrompt: request.field.whatsAppPrompt ?? null,
      whatsAppCollectionOrder: request.field.whatsAppCollectionOrder ?? null,
    };
  }

  /**
   * Biblioteca de campos reutilizáveis do projeto (`ProjectReusableField`, IsActive, escopo company+project).
   * GET /api/projects/{projectId}/reusable-workflow-fields
   */
  async getProjectReusableFields(projectId: string): Promise<ProjectReusableFieldListItem[]> {
    const raw = await firstValueFrom(
      this.http.get<unknown>(
        `/api/projects/${encodeURIComponent(projectId)}/reusable-workflow-fields`
      )
    );
    const items = this.resolveItems(raw);
    const result: ProjectReusableFieldListItem[] = [];
    for (let i = 0; i < items.length; i += 1) {
      const entry = items[i];
      const id = this.readString(this.pickValue(entry, ['id', 'Id']));
      /** DTO backend: `Label` */
      const name =
        this.readString(
          this.pickValue(entry, ['label', 'Label', 'name', 'Name', 'title', 'Title'])
        ) || id;
      const type = this.resolveWorkflowFieldTypeFromApi(entry);
      if (!id) continue;
      result.push({ id, name, type });
    }
    return result.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }

  /**
   * Cria `WorkflowField` no workflow a partir de `ProjectReusableField`.
   * POST /api/workflow-fields/from-project-reusable
   */
  async linkProjectReusableFieldToWorkflow(
    request: LinkProjectReusableFieldToWorkflowRequest
  ): Promise<void> {
    await firstValueFrom(
      this.http.post<unknown>('/api/workflow-fields/from-project-reusable', {
        workflowId: request.workflowId,
        projectReusableFieldId: request.projectReusableFieldId,
      })
    );
  }

  async updateWorkflowCustomField(
    request: UpdateWorkflowCustomFieldRequest
  ): Promise<CustomFieldViewModel | null> {
    const existingKey = this.fieldKeysById.get(request.fieldId);
    const key = existingKey || this.slugify(request.field.name);
    const payload: Record<string, unknown> = {
      id: request.fieldId,
      workflowId: request.workflowId,
      label: request.field.name.trim(),
      key,
      fieldType: this.toApiFieldType(request.field.type),
      isRequired: request.field.required === true,
      isImportable: true,
      isReadOnly: request.field.readOnly === true,
      showInQuickView: request.field.showInQuickView === true,
      showInApp: request.field.showInApp === true,
      collectViaWhatsApp: request.field.collectViaWhatsApp === true,
      whatsAppPrompt: request.field.whatsAppPrompt ?? null,
      whatsAppCollectionOrder: request.field.whatsAppCollectionOrder ?? null,
    };
    const formulaDpUpdate = clampFormulaResultDecimalPlaces(
      request.field.type === 'FORMULA' ? request.field.formulaResultDecimalPlaces : 0
    );
    if (this.toApiFieldType(request.field.type) === 14) {
      payload['formulaResultDecimalPlaces'] = formulaDpUpdate;
      if (request.field.formulaDefinition) {
        payload['formulaOperands'] = toFormulaOperandsApiPayload(request.field.formulaDefinition);
      }
    }
    if (this.toApiFieldType(request.field.type) === 15) {
      payload['ratingLevels'] = clampRatingLevels(request.field.ratingLevels);
    }
    if (this.toApiFieldType(request.field.type) === 3) {
      const numberDecimalPlaces = clampNumberFieldDecimalPlaces(
        request.field.type === 'NUMBER' ? request.field.numberDecimalPlaces : 0
      );
      const numberShowPercentSuffix =
        request.field.type === 'NUMBER' && request.field.numberShowPercentSuffix === true;
      payload['numberDecimalPlaces'] = numberDecimalPlaces;
      payload['decimalPlaces'] = numberDecimalPlaces;
      payload['numberShowPercentSuffix'] = numberShowPercentSuffix;
      payload['showPercentSuffix'] = numberShowPercentSuffix;
    }
    if (this.toApiFieldType(request.field.type) === 16) {
      payload['codePrefix'] = clampCodePrefix(request.field.codePrefix);
      payload['codeBodyFormat'] = codeFormatToApi(request.field.codeFormat);
      payload['codeBodyLength'] = normalizeCodeLength(request.field.codeLength);
      payload['codeSeparator'] = codeSeparatorToApi(request.field.codeSeparator);
    }
    /** PUT sem o campo mantém o valor atual — por isso enviamos sempre o estado do toggle. */
    if (this.toApiFieldType(request.field.type) === 9) {
      payload['personRestrictToWorkflowMembers'] =
        request.field.personRestrictToWorkflowMembers === true;
    }
    if (request.saveAsProjectReusable === true) {
      payload['saveAsProjectReusable'] = true;
    }

    await firstValueFrom(this.http.put<unknown>(`/api/workflow-fields/${request.fieldId}`, payload));

    if (request.field.type === 'NUMBER') {
      this.recordNumberFieldOverlayFromSave(request.fieldId, request.field);
    } else {
      this.clearNumberFieldOverlay(request.fieldId);
    }
    if (request.field.type === 'FORMULA') {
      this.recordFormulaFieldOverlay(request.fieldId, formulaDpUpdate);
    } else {
      this.clearFormulaFieldOverlay(request.fieldId);
    }

    if (request.field.type === 'FORMULA' && request.field.formulaDefinition) {
      await this.putFormulaOperands(
        request.fieldId,
        request.field.formulaDefinition,
        formulaDpUpdate
      );
    }

    if ([6, 21].includes(payload['fieldType'] as number)) {
      try {
        await this.syncSelectOptionsDesiredState(
          request.fieldId,
          request.field.options.map((o, idx) => ({
            id: o.id,
            label: o.label,
            value: o.value,
            colorHex: o.colorHex,
            order: o.order ?? idx + 1,
          }))
        );
      } catch {
        await this.syncOptions(request.fieldId, request.field.options);
      }
    }

    const response = await this.getWorkflowCustomFields(request.workflowId);
    return response.fields.find((field) => field.fieldId === request.fieldId) ?? null;
  }

  private normalizeNumberFieldOverlayKey(fieldId: string): string {
    return fieldId.trim().toLowerCase().replace(/[{}]/g, '');
  }

  private recordNumberFieldOverlayFromSave(
    fieldId: string,
    saved: Pick<CustomFieldViewModel, 'type' | 'numberDecimalPlaces' | 'numberShowPercentSuffix'>
  ): void {
    if (saved.type !== 'NUMBER') return;
    const key = this.normalizeNumberFieldOverlayKey(fieldId);
    if (!key) return;
    this.numberFieldOverlayById.set(key, {
      numberDecimalPlaces: clampNumberFieldDecimalPlaces(saved.numberDecimalPlaces),
      numberShowPercentSuffix: saved.numberShowPercentSuffix === true,
    });
  }

  private clearNumberFieldOverlay(fieldId: string): void {
    this.numberFieldOverlayById.delete(this.normalizeNumberFieldOverlayKey(fieldId));
  }

  private recordFormulaFieldOverlay(fieldId: string, decimalPlaces: number): void {
    const key = this.normalizeNumberFieldOverlayKey(fieldId);
    if (!key) return;
    this.formulaResultDecimalPlacesOverlayById.set(
      key,
      clampFormulaResultDecimalPlaces(decimalPlaces)
    );
  }

  private clearFormulaFieldOverlay(fieldId: string): void {
    this.formulaResultDecimalPlacesOverlayById.delete(this.normalizeNumberFieldOverlayKey(fieldId));
    this.formulaDetailIdByWorkflowFieldId.delete(this.normalizeNumberFieldOverlayKey(fieldId));
  }

  private applyNumberFieldOverlaysToList(fields: CustomFieldViewModel[]): CustomFieldViewModel[] {
    return fields.map((f) => this.applyNumberFieldOverlayToField(f));
  }

  private applyNumberFieldOverlayToField(f: CustomFieldViewModel): CustomFieldViewModel {
    if (f.type === 'FORMULA') {
      const overlay = this.formulaResultDecimalPlacesOverlayById.get(
        this.normalizeNumberFieldOverlayKey(f.fieldId)
      );
      if (overlay == null) return f;
      return {
        ...f,
        formulaResultDecimalPlaces: clampFormulaResultDecimalPlaces(overlay),
      };
    }
    if (f.type !== 'NUMBER') return f;
    const o = this.numberFieldOverlayById.get(this.normalizeNumberFieldOverlayKey(f.fieldId));
    if (!o) return f;
    return {
      ...f,
      numberDecimalPlaces: o.numberDecimalPlaces,
      numberShowPercentSuffix: o.numberShowPercentSuffix,
    };
  }

  async deleteWorkflowCustomField(request: DeleteWorkflowCustomFieldRequest): Promise<void> {
    await firstValueFrom(this.http.delete(`/api/workflow-fields/${request.fieldId}`));
    this.clearNumberFieldOverlay(request.fieldId);
    this.clearFormulaFieldOverlay(request.fieldId);
    this.fieldKeysById.delete(request.fieldId);
    const current = this.schemaSignal()[request.workflowId] ?? [];
    this.schemaSignal.update((schema) => ({
      ...schema,
      [request.workflowId]: current.filter((item) => item.fieldId !== request.fieldId),
    }));
  }

  /** PUT /api/custom-fields/{id}/formula — operandos + opcional resultDecimalPlaces (0–4). */
  private async putFormulaOperands(
    fieldId: string,
    definition: FormulaDefinition,
    resultDecimalPlaces: number
  ): Promise<void> {
    const places = clampFormulaResultDecimalPlaces(resultDecimalPlaces);
    const candidates = [
      this.formulaDetailIdByWorkflowFieldId.get(this.normalizeNumberFieldOverlayKey(fieldId)) ?? '',
      fieldId,
    ].filter((id, idx, arr) => !!id && arr.indexOf(id) === idx);

    let lastError: unknown = null;
    for (const candidateId of candidates) {
      try {
        await firstValueFrom(
          this.http.put(`/api/custom-fields/${encodeURIComponent(candidateId)}/formula`, {
            operands: toFormulaOperandsApiPayload(definition),
            resultDecimalPlaces: places,
          })
        );
        if (candidateId !== fieldId) {
          this.formulaDetailIdByWorkflowFieldId.set(
            this.normalizeNumberFieldOverlayKey(fieldId),
            candidateId
          );
        }
        return;
      } catch (error) {
        lastError = error;
      }
    }
    if (lastError) throw lastError;
  }

  private isPlaceholderWorkflowFieldId(id: string): boolean {
    const t = (id ?? '').trim().toLowerCase();
    return !t || t === PLACEHOLDER_WORKFLOW_FIELD_ID;
  }

  /**
   * Campos nativos do card (título, descrição, responsável) não são “campos personalizados”.
   * Quando o back os cria por engano ao salvar o formulário (ex. #10001…), escondemos da lista
   * de configuração para não duplicar no card — o produto continua a usar os campos padrão do item.
   */
  private shouldHideAsNativeCardArtifact(entry: unknown): boolean {
    const row = this.asRecord(entry);
    const nested = this.asRecord(row['field'] ?? row['Field']);
    const merged: Record<string, unknown> = { ...nested, ...row };

    const sourceKind = this.readString(this.pickValue(merged, ['sourceKind', 'SourceKind']))
      .replace(/\s+/g, '')
      .toLowerCase();
    if (sourceKind === 'nativecardfield') {
      return true;
    }

    const apiKey = this.readString(this.pickValue(merged, ['key', 'Key'])).trim();
    const nativeKey = this.readString(
      this.pickValue(merged, ['nativeCardFieldKey', 'NativeCardFieldKey'])
    ).trim();
    for (const raw of [apiKey, nativeKey]) {
      if (!raw) continue;
      const n = this.normalizeKeyForNativeLeakCheck(raw);
      if (
        n === 'title' ||
        n === 'cardtitle' ||
        n === 'description' ||
        n === 'carddescription' ||
        n === 'owneruserid' ||
        n === 'assigneeuserid' ||
        n === 'assignee' ||
        n === 'owner' ||
        n === 'assignedto'
      ) {
        return true;
      }
    }
    return false;
  }

  private normalizeKeyForNativeLeakCheck(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  /**
   * Alguns DTOs devolvem opções embutidas no próprio campo; usamos quando
   * `/api/workflow-field-options/field/{id}` ainda não tem linhas (ex.: vínculo reutilizável).
   */
  private parseInlineWorkflowFieldOptions(source: Record<string, unknown>): Array<{
    id: string;
    label: string;
    value: string;
    colorHex: string;
    order: number;
  }> {
    const tryReadArray = (src: Record<string, unknown>): unknown[] | null => {
      for (const key of [
        'options',
        'Options',
        'workflowFieldOptions',
        'WorkflowFieldOptions',
        'selectOptions',
        'SelectOptions',
      ] as const) {
        const v = this.pickValue(src, [key]);
        if (Array.isArray(v) && v.length) return v;
      }
      return null;
    };

    let raw = tryReadArray(source);
    if (!raw) {
      const nested = this.asRecord(this.pickValue(source, ['field', 'Field']));
      if (Object.keys(nested).length) {
        raw = tryReadArray(nested);
      }
    }
    if (!raw?.length) return [];

    return raw
      .map((entry, idx) => {
        const e = this.asRecord(entry);
        const label = this.readString(
          String(this.pickValue(e, ['label', 'Label', 'name', 'Name']) ?? '')
        );
        const value =
          this.readString(String(this.pickValue(e, ['value', 'Value']) ?? '')) ||
          this.slugify(label) ||
          `option_${idx + 1}`;
        const id = this.readString(e['id'] ?? e['Id']) || `__inline_${idx}_${value}`;
        return {
          id,
          label,
          value,
          colorHex: this.normalizeColorHex(
            this.readString(String(this.pickValue(e, ['colorHex', 'ColorHex']) ?? ''))
          ),
          order: this.readNumber(this.pickValue(e, ['order', 'Order']), idx + 1),
        };
      })
      .filter((o) => !!o.label)
      .sort((a, b) => a.order - b.order);
  }

  /**
   * SELECT com vínculo à biblioteca: mesma URL; a API pode devolver ids de opção da biblioteca
   * (compartilhados entre workflows). PUT/POST/DELETE em `syncOptions`/`deleteOption` usam esse id +
   * `workflowFieldId` como hoje — não tratar opções como cópias independentes por workflow.
   */
  private async loadOptions(workflowFieldId: string) {
    if (this.isPlaceholderWorkflowFieldId(workflowFieldId)) return [];
    const response = await firstValueFrom(
      this.http.get<unknown>(`/api/workflow-field-options/field/${workflowFieldId}`)
    );
    return this.resolveItems(response)
      .map((entry, index) => ({
        id: this.readString(entry['id']),
        label: this.readString(entry['label']),
        value:
          this.readString(entry['value']) ||
          this.slugify(this.readString(entry['label'])) ||
          `option_${index + 1}`,
        colorHex: this.normalizeColorHex(this.readString(entry['colorHex'])),
        order: this.readNumber(entry['order'], index + 1),
      }))
      .filter((option) => !!option.id && !!option.label)
      .sort((a, b) => a.order - b.order);
  }

  private async syncOptions(
    workflowFieldId: string,
    desired: Array<{ id: string; label: string; value: string; colorHex: string; order: number }>
  ): Promise<void> {
    const current = await this.loadOptions(workflowFieldId);
    const currentById = new Map(current.map((item) => [item.id, item] as const));
    const desiredIds = new Set(desired.map((item) => item.id).filter(Boolean));

    for (let index = 0; index < desired.length; index += 1) {
      const option = desired[index];
      const order = index + 1;
      if (option.id && currentById.has(option.id)) {
        await this.createOrUpdateOption(
          option.id,
          workflowFieldId,
          option.label,
          option.value || null,
          option.colorHex,
          order
        );
      } else {
        await this.createOrUpdateOption(
          null,
          workflowFieldId,
          option.label,
          option.value || null,
          option.colorHex,
          order
        );
      }
    }

    for (const option of current) {
      if (desiredIds.has(option.id)) continue;
      await this.deleteOption(option.id, workflowFieldId);
    }
  }

  private async createOrUpdateOption(
    optionId: string | null,
    workflowFieldId: string,
    label: string,
    value: string | null,
    colorHex: string,
    order: number
  ): Promise<void> {
    const payload = {
      ...(optionId ? { id: optionId } : {}),
      workflowFieldId,
      label: label.trim(),
      value: value && value.trim().length > 0 ? value.trim() : null,
      colorHex: this.normalizeColorHex(colorHex),
      order: Math.max(1, Math.trunc(order || 1)),
    };
    if (optionId) {
      await firstValueFrom(this.http.put(`/api/workflow-field-options/${optionId}`, payload));
      return;
    }
    await firstValueFrom(this.http.post('/api/workflow-field-options', payload));
  }

  private async deleteOption(optionId: string, workflowFieldId: string): Promise<void> {
    const encodedFieldId = encodeURIComponent(workflowFieldId);
    await firstValueFrom(
      this.http.delete(`/api/workflow-field-options/${optionId}?workflowFieldId=${encodedFieldId}`)
    );
  }

  /**
   * Contrato API costuma enviar `fieldType` como número, string numérica ("9") ou nome de enum ("Person").
   * Sem isso, valores string caem no default e viram TEXT — quebra renderização (ex.: Pessoa como input livre).
   */
  private resolveWorkflowFieldTypeFromApi(source: Record<string, unknown>): CustomFieldViewModel['type'] {
    const primary = this.pickValue(source, ['fieldType', 'FieldType']);
    if (typeof primary === 'number' && Number.isFinite(primary)) {
      return this.fromApiFieldType(primary);
    }
    if (typeof primary === 'string') {
      const t = primary.trim();
      if (t.length) {
        const asNum = Number(t);
        if (Number.isFinite(asNum)) return this.fromApiFieldType(asNum);
        const byName = this.fieldTypeFromApiEnumString(t);
        if (byName) return byName;
      }
    }
    const secondary = this.pickValue(source, ['type', 'Type']);
    if (typeof secondary === 'string' && secondary.trim()) {
      const byName = this.fieldTypeFromApiEnumString(secondary);
      if (byName) return byName;
    }
    return 'TEXT';
  }

  private fieldTypeFromApiEnumString(raw: string): CustomFieldViewModel['type'] | null {
    const k = raw
      .trim()
      .replace(/^FieldType\./i, '')
      .replace(/\s+/g, '_')
      .toLowerCase();
    const map: Record<string, CustomFieldViewModel['type']> = {
      text: 'TEXT',
      long_text: 'LONG_TEXT',
      longtext: 'LONG_TEXT',
      number: 'NUMBER',
      date: 'DATE',
      boolean: 'BOOLEAN',
      checkbox: 'CHECKBOX',
      select: 'SELECT',
      rowcolor: 'ROW_COLOR',
      row_color: 'ROW_COLOR',
      currency: 'CURRENCY',
      progress: 'PROGRESS',
      person: 'PERSON',
      user: 'USER',
      phone: 'PHONE',
      email: 'EMAIL',
      file: 'FILE',
      labels: 'LABELS',
      formula: 'FORMULA',
      multi_select: 'MULTI_SELECT',
      multiselect: 'MULTI_SELECT',
      rating: 'RATING',
      code: 'CODE',
      datetime: 'DATETIME',
      time: 'TIME',
      confirmationdate: 'CONFIRMATION_DATE',
      confirmation_date: 'CONFIRMATION_DATE',
      itemlist: 'ITEM_LIST',
      item_list: 'ITEM_LIST',
    };
    return map[k] ?? null;
  }

  private fromApiFieldType(fieldType: number): CustomFieldViewModel['type'] {
    if (fieldType === 2) return 'LONG_TEXT';
    if (fieldType === 3) return 'NUMBER';
    if (fieldType === 4) return 'DATE';
    if (fieldType === 5) return 'BOOLEAN';
    if (fieldType === 21) return 'ROW_COLOR';
    if (fieldType === 6) return 'SELECT';
    if (fieldType === 7) return 'CURRENCY';
    if (fieldType === 8) return 'PROGRESS';
    if (fieldType === 9) return 'PERSON';
    if (fieldType === 10) return 'PHONE';
    if (fieldType === 11) return 'EMAIL';
    if (fieldType === 12) return 'FILE';
    if (fieldType === 13) return 'LABELS';
    if (fieldType === 14) return 'FORMULA';
    if (fieldType === 15) return 'RATING';
    if (fieldType === 16) return 'CODE';
    if (fieldType === 17) return 'DATETIME';
    if (fieldType === 18) return 'TIME';
    if (fieldType === 19) return 'CONFIRMATION_DATE';
    if (fieldType === 20) return 'ITEM_LIST';
    return 'TEXT';
  }

  private toApiFieldType(fieldType: CustomFieldViewModel['type']): number {
    if (fieldType === 'ROW_COLOR') return 21;
    if (fieldType === 'LONG_TEXT') return 2;
    if (fieldType === 'NUMBER') return 3;
    if (fieldType === 'DATE') return 4;
    if (fieldType === 'BOOLEAN' || fieldType === 'CHECKBOX') return 5;
    if (fieldType === 'SELECT' || fieldType === 'MULTI_SELECT' || fieldType === 'USER') return 6;
    if (fieldType === 'CURRENCY') return 7;
    if (fieldType === 'PROGRESS') return 8;
    if (fieldType === 'PERSON') return 9;
    if (fieldType === 'PHONE') return 10;
    if (fieldType === 'EMAIL') return 11;
    if (fieldType === 'FILE') return 12;
    if (fieldType === 'LABELS') return 13;
    if (fieldType === 'FORMULA') return 14;
    if (fieldType === 'RATING') return 15;
    if (fieldType === 'CODE') return 16;
    if (fieldType === 'DATETIME') return 17;
    if (fieldType === 'TIME') return 18;
    if (fieldType === 'CONFIRMATION_DATE') return 19;
    if (fieldType === 'ITEM_LIST') return 20;
    return 1;
  }

  private resolveItems(raw: unknown): Record<string, unknown>[] {
    if (Array.isArray(raw)) {
      return raw.map((item) => this.asRecord(item));
    }
    const root = this.asRecord(raw);
    const items = this.readArray(root['items'] ?? root['Items']);
    if (items.length) return items.map((item) => this.asRecord(item));
    const entries = this.readArray(root['entries'] ?? root['Entries']);
    if (entries.length) return entries.map((item) => this.asRecord(item));
    const fields = this.readArray(root['fields'] ?? root['Fields']);
    if (fields.length) return fields.map((item) => this.asRecord(item));
    const data = this.asRecord(root['data'] ?? root['Data']);
    const nested = this.readArray(data['items'] ?? data['Items']);
    if (nested.length) return nested.map((item) => this.asRecord(item));
    const dataEntries = this.readArray(data['entries'] ?? data['Entries']);
    if (dataEntries.length) return dataEntries.map((item) => this.asRecord(item));
    return this.readArray(data['fields'] ?? data['Fields']).map((item) => this.asRecord(item));
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  }

  private readArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
  }

  private readString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private readNumber(value: unknown, fallback: number): number {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const t = value.trim();
      if (!t.length) return fallback;
      const n = Number(t);
      if (Number.isFinite(n)) return n;
    }
    return fallback;
  }

  private readBoolean(value: unknown): boolean {
    if (value === true) return true;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1';
    }
    return false;
  }

  private slugify(value: string): string {
    return value
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 64);
  }

  private normalizeColorHex(value: string): string {
    const normalized = value.trim().toUpperCase();
    return /^#[0-9A-F]{6}$/.test(normalized) ? normalized : '#E2E8F0';
  }

  private pickValue(source: Record<string, unknown>, keys: string[]): unknown {
    for (const key of keys) {
      if (key in source) return source[key];

      const normalizedTarget = this.normalizeKey(key);
      for (const [candidateKey, candidateValue] of Object.entries(source)) {
        if (this.normalizeKey(candidateKey) === normalizedTarget) {
          return candidateValue;
        }
      }
    }
    return undefined;
  }

  private pickBooleanLikeValue(source: Record<string, unknown>, keys: string[]): unknown {
    const direct = this.pickValue(source, keys);
    if (direct !== undefined) return direct;

    // Fallback robusto: procura recursivamente ate profundidade 3
    // por chaves equivalentes (case-insensitive e sem separadores).
    const targetKeys = new Set(keys.map((key) => this.normalizeKey(key)));
    return this.findValueByNormalizedKeys(source, targetKeys, 3);
  }

  private findValueByNormalizedKeys(
    source: Record<string, unknown>,
    targetKeys: Set<string>,
    maxDepth: number,
    depth = 0
  ): unknown {
    for (const [key, value] of Object.entries(source)) {
      if (targetKeys.has(this.normalizeKey(key))) return value;
      if (depth >= maxDepth) continue;
      const nested = this.asRecord(value);
      if (Object.keys(nested).length === 0) continue;
      const fromNested = this.findValueByNormalizedKeys(nested, targetKeys, maxDepth, depth + 1);
      if (fromNested !== undefined) return fromNested;
    }
    return undefined;
  }

  private normalizeKey(value: string): string {
    return value.replace(/[_\-\s]/g, '').toLowerCase();
  }

  /** Extrai mensagem legível de erros HTTP (ProblemDetails, ASP.NET validation, texto plano). */
  extractApiErrorMessage(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse)) return null;
    const body = error.error;
    if (typeof body === 'string' && body.trim()) return this.clipErrorText(body.trim());
    if (body && typeof body === 'object') {
      const rec = body as Record<string, unknown>;
      const errors = rec['errors'];
      // API Taskeo: { errors: ["msg"] } — priorizar antes de "message" genérica
      if (Array.isArray(errors) && errors.length > 0) {
        const first = errors[0];
        if (typeof first === 'string' && first.trim()) return this.clipErrorText(first.trim());
      }
      if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
        for (const val of Object.values(errors)) {
          if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'string') {
            return this.clipErrorText(val[0]);
          }
        }
      }
      for (const key of ['message', 'title', 'detail'] as const) {
        const v = rec[key];
        if (typeof v === 'string' && v.trim()) return this.clipErrorText(v.trim());
      }
    }
    if (error.status && error.statusText) {
      return `${error.status} ${error.statusText}`;
    }
    return null;
  }

  /**
   * PUT /api/workflow-field-options/field/{workflowFieldId}/sync — corpo = estado desejado.
   */
  async syncSelectOptionsDesiredState(
    workflowFieldId: string,
    options: Array<{
      id?: string | null;
      label: string;
      value?: string | null;
      colorHex?: string | null;
      order: number;
    }>
  ): Promise<unknown> {
    const body = {
      options: options.map((o, idx) => ({
        ...(o.id?.trim() ? { id: o.id.trim() } : {}),
        label: o.label,
        ...(o.value != null && String(o.value).trim() ? { value: String(o.value).trim() } : {}),
        ...(o.colorHex?.trim() ? { colorHex: o.colorHex.trim() } : {}),
        order: Number.isFinite(o.order) ? o.order : idx + 1,
      })),
    };
    return await firstValueFrom(
      this.http.put<unknown>(
        `/api/workflow-field-options/field/${encodeURIComponent(workflowFieldId)}/sync`,
        body
      )
    );
  }

  private clipErrorText(text: string): string {
    const max = 500;
    return text.length > max ? `${text.slice(0, max)}…` : text;
  }
}

const REQUIRED_KEYS = [
  'isRequired',
  'IsRequired',
  'required',
  'Required',
  'isMandatory',
  'IsMandatory',
  'mandatory',
  'Mandatory',
];

const QUICK_VIEW_KEYS = ['showInQuickView', 'ShowInQuickView', 'isQuickView', 'IsQuickView'];

const SHOW_IN_APP_KEYS = ['showInApp', 'ShowInApp', 'isVisibleInApp', 'IsVisibleInApp'];

const READONLY_KEYS = [
  'isReadOnly',
  'IsReadOnly',
  'readOnly',
  'ReadOnly',
  'isImmutable',
  'IsImmutable',
];
